import {useMemo} from 'react';
import {useFetchPaymentWays} from '@rocket-ui/lk-api';
import {
	canSkipResidentCodeStep,
	formatRecipientFullName,
	getContinueButtonLabel,
	getPostInnFlowConfig,
	getRegistrationProgress,
	getStatusConnectionProgress,
	hasConnectedWorkStatus,
	isDocumentDetailsStepValid,
	isDocumentPhotosStepValid,
	isDocumentsRegistrationStep,
	isInnStepValid,
	isMyTaxStep,
	isOptionalInnValid,
	isPaymentDetailsStepValid,
	isPersonalInfoStepValid,
	isRegistrationStepVisible,
	isResidenceDocumentStepValid,
	isResidentCodeStepValidForRequirement,
	isSnilsStepValidForRequirement,
	isTaxIdentifierStepValid,
	MyTaxNameMismatchInfo,
	shouldSkipIdentityVerification,
	RegistrationStep,
	resolveDocumentsPassportPreviewUrls,
	resolveResidenceDocumentPreviewUrl,
	combinesDocumentPhotosWithDetails as resolvesCombinesDocumentPhotosWithDetails,
	usesForeignDocumentDetailsForm,
	WORK_STATUS_STEP_ENABLED,
} from 'features/registration';
import type {RegistrationWizardState} from './useRegistrationWizardState';

/** Производные от стейта значения: превью документов, прогресс шапки, валидность шага и видимость футеров. */
export const useRegistrationDerived = (state: RegistrationWizardState) => {
	const {
		backendFlags,
		documentsFlow,
		documentsUpload,
		foreignDocumentDetails,
		hasSnilsSaved,
		identificationDocumentPhotos,
		identityEditSnapshot,
		innStep,
		myTaxNameMismatch,
		passportDetails,
		paymentDetails,
		paymentSubStep,
		pendingPaymentMethod,
		personalInfo,
		registrationFlow,
		residentCodeStep,
		returnToIdentityReview,
		selfEmploymentPartner,
		snilsStep,
		statusConnectionIncludesInn,
		statusConnectionIncludesSnils,
		step,
		taxIdentifierStep,
		workStatus,
		workStatusConnections,
	} = state;

	const documentPreviewUrls = useMemo(
		() =>
			resolveDocumentsPassportPreviewUrls(
				documentsFlow,
				documentsUpload,
				identificationDocumentPhotos,
			),
		[documentsFlow, documentsUpload, identificationDocumentPhotos],
	);

	const residencePreviewUrl = useMemo(
		() => resolveResidenceDocumentPreviewUrl(documentsUpload, identificationDocumentPhotos),
		[documentsUpload, identificationDocumentPhotos],
	);

	const usesForeignDocumentDetails = usesForeignDocumentDetailsForm(
		personalInfo.citizenshipOksmCode,
		personalInfo.residenceOksmCode,
	);
	const combinesDocumentPhotosWithDetails = resolvesCombinesDocumentPhotosWithDetails(
		personalInfo.citizenshipOksmCode,
	);

	const passportStepValidationOptions = useMemo(
		() => ({
			useForeignDocumentDetails: usesForeignDocumentDetails,
			russianDetails: passportDetails,
			foreignDetails: foreignDocumentDetails,
			citizenshipOksmCode: personalInfo.citizenshipOksmCode,
			residenceOksmCode: personalInfo.residenceOksmCode,
		}),
		[
			usesForeignDocumentDetails,
			passportDetails,
			foreignDocumentDetails,
			personalInfo.citizenshipOksmCode,
			personalInfo.residenceOksmCode,
		],
	);

	const postInnFlow = useMemo(() => {
		const flow = hasSnilsSaved ? {...registrationFlow, snils: 'hidden' as const} : registrationFlow;

		return getPostInnFlowConfig(workStatus, {registrationFlow: flow, selfEmploymentProvider: selfEmploymentPartner});
	}, [hasSnilsSaved, registrationFlow, selfEmploymentPartner, workStatus]);
	const includeDocumentPhotosStep = usesForeignDocumentDetails && !combinesDocumentPhotosWithDetails;
	const registrationProgress = getRegistrationProgress(
		step,
		Boolean(documentsFlow.residence),
		includeDocumentPhotosStep,
	);
	/** ИП: БИК + Счёт + Подтверждение — один счётчик. ИНН уже введён раньше. */
	const statusConnectionProgress = getStatusConnectionProgress(step, workStatus, registrationFlow, {
		includeInn: statusConnectionIncludesInn,
		includeSnils: statusConnectionIncludesSnils,
		paymentMethod: pendingPaymentMethod,
		paymentSubStep,
	});
	const isStatusConnectionStep =
		step === RegistrationStep.Inn ||
		step === RegistrationStep.Snils ||
		step === RegistrationStep.ResidentCode ||
		step === RegistrationStep.PaymentMethod;
	const isStatusConnectionFlow = isStatusConnectionStep || isMyTaxStep(step);
	const isEditingFromIdentityReview =
		returnToIdentityReview &&
		(step === RegistrationStep.PersonalInfo ||
			step === RegistrationStep.DocumentPhotos ||
			step === RegistrationStep.ResidenceDocument ||
			step === RegistrationStep.DocumentDetails);
	/** Пагинация статуса — на экранах из getStatusConnectionSteps; остальное (ошибки/успех) — только крестик. */
	const useCloseHeader =
		(isStatusConnectionFlow && !statusConnectionProgress) ||
		isEditingFromIdentityReview ||
		(registrationFlow.taxIdType === 'foreign_tax_id' && step === RegistrationStep.Inn);
	const headerProgress = statusConnectionProgress ?? registrationProgress;

	const isPaymentFlowActive =
		step === RegistrationStep.PaymentMethod && pendingPaymentMethod !== null && paymentSubStep !== 'select';

	const {data: existingPaymentWays = []} = useFetchPaymentWays({
		enabled: step === RegistrationStep.PaymentMethod && isPaymentFlowActive,
	});
	const hasExistingPaymentWays = existingPaymentWays.length > 0;

	const isDocumentsFlowActive = isDocumentsRegistrationStep(step);
	const isIdentityReviewStep = step === RegistrationStep.IdentityReview;
	const isMyTaxIntroStep = step === RegistrationStep.MyTaxIntro;
	const isMyTaxSuccessStep = step === RegistrationStep.MyTaxSuccess;
	const isMyTaxAccessErrorStep = step === RegistrationStep.MyTaxAccessError;
	const isMyTaxNotSelfEmployedStep = step === RegistrationStep.MyTaxNotSelfEmployed;
	const isMyTaxLoadErrorStep = step === RegistrationStep.MyTaxLoadError;
	const isRegistrationCompleteStep = step === RegistrationStep.RegistrationComplete;

	const isCurrentStepValid = useMemo(() => {
		if (step === RegistrationStep.PersonalInfo) {
			const innOnStep = returnToIdentityReview && !usesForeignDocumentDetails;

			return isPersonalInfoStepValid(personalInfo) && (!innOnStep || isOptionalInnValid(innStep));
		}

		if (step === RegistrationStep.DocumentPhotos) {
			return isDocumentPhotosStepValid(documentsFlow, documentsUpload);
		}

		if (step === RegistrationStep.ResidenceDocument) {
			return isResidenceDocumentStepValid(documentsFlow, documentsUpload);
		}

		if (step === RegistrationStep.DocumentDetails) {
			const detailsValid = isDocumentDetailsStepValid(passportStepValidationOptions);

			if (combinesDocumentPhotosWithDetails) {
				/** Фото документов — на том же экране, что и поля. Проживание — отдельный шаг. */
				return detailsValid && isDocumentPhotosStepValid(documentsFlow, documentsUpload);
			}

			return detailsValid;
		}

		if (step === RegistrationStep.IdentityReview) {
			return true;
		}

		if (step === RegistrationStep.WorkStatus) {
			return WORK_STATUS_STEP_ENABLED && hasConnectedWorkStatus(workStatusConnections);
		}

		if (step === RegistrationStep.IdentificationInn) {
			return registrationFlow.taxIdType === 'russian_inn'
				? isInnStepValid(innStep)
				: isTaxIdentifierStepValid(taxIdentifierStep);
		}

		if (step === RegistrationStep.Inn) {
			return registrationFlow.taxIdType === 'russian_inn'
				? isInnStepValid(innStep)
				: isTaxIdentifierStepValid(taxIdentifierStep);
		}

		if (step === RegistrationStep.Snils) {
			return isSnilsStepValidForRequirement(registrationFlow.snils, snilsStep);
		}

		if (step === RegistrationStep.ResidentCode) {
			return isResidentCodeStepValidForRequirement(registrationFlow.residentCode, residentCodeStep);
		}

		if (isPaymentFlowActive && pendingPaymentMethod) {
			return isPaymentDetailsStepValid(pendingPaymentMethod, paymentSubStep, paymentDetails, workStatus);
		}

		return false;
	}, [
		documentsFlow,
		documentsUpload,
		innStep,
		isPaymentFlowActive,
		registrationFlow,
		snilsStep,
		taxIdentifierStep,
		passportStepValidationOptions,
		paymentDetails,
		paymentSubStep,
		pendingPaymentMethod,
		personalInfo,
		residentCodeStep,
		returnToIdentityReview,
		step,
		combinesDocumentPhotosWithDetails,
		usesForeignDocumentDetails,
		workStatus,
		workStatusConnections,
	]);

	const isProfilePhotosEdit =
		returnToIdentityReview &&
		!identityEditSnapshot &&
		(step === RegistrationStep.DocumentPhotos ||
			(step === RegistrationStep.DocumentDetails && combinesDocumentPhotosWithDetails));

	const continueButtonLabel =
		step === RegistrationStep.IdentityReview || isProfilePhotosEdit
			? shouldSkipIdentityVerification(backendFlags)
				? 'Все окей'
				: 'Отправить на проверку'
			: isEditingFromIdentityReview
				? 'Сохранить'
				: isPaymentFlowActive
					? getContinueButtonLabel(paymentSubStep)
					: 'Продолжить';

	const showContinueFooter =
		isCurrentStepValid &&
		step !== RegistrationStep.ResidentCode &&
		step !== RegistrationStep.MyTaxChecking &&
		step !== RegistrationStep.PartnerAgreementChecking &&
		step !== RegistrationStep.PartnerAgreementPending &&
		step !== RegistrationStep.MyTaxSuccess &&
		step !== RegistrationStep.MyTaxNameMismatch &&
		step !== RegistrationStep.MyTaxAccessError &&
		step !== RegistrationStep.MyTaxNotSelfEmployed &&
		step !== RegistrationStep.MyTaxFnsPending &&
		step !== RegistrationStep.MyTaxLoadError &&
		(step !== RegistrationStep.PaymentMethod || isPaymentFlowActive);

	const showResidentCodeFooter = step === RegistrationStep.ResidentCode;
	const canSkipResidentCode = canSkipResidentCodeStep(registrationFlow.residentCode);
	const residentCodeContinueVisible =
		showResidentCodeFooter &&
		isResidentCodeStepValidForRequirement(registrationFlow.residentCode, residentCodeStep) &&
		!residentCodeStep.skipped;

	const resolvedMyTaxNameMismatch = useMemo<MyTaxNameMismatchInfo>(() => {
		if (myTaxNameMismatch) {
			return myTaxNameMismatch;
		}

		return {
			inn: innStep.inn,
			fullName: formatRecipientFullName(personalInfo),
		};
	}, [innStep.inn, myTaxNameMismatch, personalInfo]);

	return {
		canSkipResidentCode,
		continueButtonLabel,
		documentPreviewUrls,
		hasExistingPaymentWays,
		headerProgress,
		isCurrentStepValid,
		isDocumentsFlowActive,
		isEditingFromIdentityReview,
		isIdentityReviewStep,
		isMyTaxAccessErrorStep,
		isMyTaxIntroStep,
		isMyTaxLoadErrorStep,
		isMyTaxNotSelfEmployedStep,
		isMyTaxSuccessStep,
		isPaymentFlowActive,
		isRegistrationCompleteStep,
		isStatusConnectionFlow,
		isStatusConnectionStep,
		passportStepValidationOptions,
		postInnFlow,
		registrationProgress,
		residencePreviewUrl,
		residentCodeContinueVisible,
		resolvedMyTaxNameMismatch,
		showContinueFooter,
		showResidentCodeFooter,
		statusConnectionProgress,
		useCloseHeader,
		combinesDocumentPhotosWithDetails,
		usesForeignDocumentDetails,
	};
};

export type RegistrationDerived = ReturnType<typeof useRegistrationDerived>;
